/**
 * AddReminderModal Component
 * Modal for creating a growth reminder with AI image generation.
 * Same UI as AiImageGenerator modal.
 */

import { useQuery } from "@tanstack/react-query"
import { ArrowLeft, HelpCircle, Loader2, MoreVertical, Sparkles } from "lucide-react"
import { useEffect, useState } from "react"
import { toast } from "sonner"
import { Carousel } from "@/components/carousel/Carousel"
import { AIGeneratedIcon } from "@/components/icons"
import { RitualityIcon } from "@/components/shared/RitualityIcon"
import { Button } from "@/components/ui/button"

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { useGenerateImage } from "@/features/media/hooks/useGenerateImage"
import type { GeneratedImage } from "@/features/media/services/ai-image.service"
import { getRecentImages, markImageUsed } from "@/features/media/services/ai-image.service"
import { useIsMobile } from "@/hooks/useIsMobile"
import { useTranslation } from "@/language"
import { useGrowthReminders } from "../hooks/useGrowthReminders"

const MAX_TEXT_LENGTH = 80

const MIME_TO_EXT: Record<string, string> = {
  "image/jpeg": "jpg",
  "image/png": "png",
  "image/webp": "webp",
}

async function base64ToFile(base64: string, mimeType: string, index: number): Promise<File> {
  const res = await fetch(`data:${mimeType};base64,${base64}`)
  const blob = await res.blob()
  const extension = MIME_TO_EXT[mimeType] || "png"
  return new File([blob], `reminder-${Date.now()}-${index}.${extension}`, { type: mimeType })
}

interface AddReminderModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  defaultText?: string
}

export function AddReminderModal({ open, onOpenChange, defaultText = "" }: AddReminderModalProps) {
  const isMobile = useIsMobile()
  const { t } = useTranslation("myRituality")
  const { createReminder, isCreating } = useGrowthReminders()
  const { mutateAsync, error } = useGenerateImage()

  const [text, setText] = useState("")
  const [isLoading, setIsLoading] = useState(false)
  const [generatedImages, setGeneratedImages] = useState<GeneratedImage[]>([])
  const [generationId, setGenerationId] = useState<string | null>(null)
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null)
  const [selectedRecentId, setSelectedRecentId] = useState<string | null>(null)
  const [hasGenerated, setHasGenerated] = useState(false)

  const { data: recentImages = [] } = useQuery({
    queryKey: ["ai-generated-images", "recent"],
    queryFn: () => getRecentImages(5),
    enabled: open,
    staleTime: 0,
  })

  useEffect(() => {
    if (open) {
      setText(defaultText)
      setIsLoading(false)
      setGeneratedImages([])
      setGenerationId(null)
      setSelectedIndex(null)
      setSelectedRecentId(null)
      setHasGenerated(false)
    }
  }, [open, defaultText])

  const handleGenerate = async () => {
    if (!text.trim()) return

    setIsLoading(true)
    setSelectedIndex(null)
    setSelectedRecentId(null)

    try {
      const result = await mutateAsync({
        entityType: "growth-reminder",
        prompt: text.trim(),
        count: 1,
      })
      setGeneratedImages(result.images)
      setGenerationId(result.generationId)
      setHasGenerated(true)
      // Auto-select the image
      setSelectedIndex(0)
    } catch {
      // Error handled by hook
    } finally {
      setIsLoading(false)
    }
  }

  const handleUseImage = async () => {
    if (selectedIndex === null || !generatedImages[selectedIndex] || !generationId) return
    if (selectedRecentId) return

    const img = generatedImages[selectedIndex]
    const file = await base64ToFile(img.base64, img.mimeType, selectedIndex)
    await markImageUsed(generationId, selectedIndex)
    createReminder(
      { text: text.trim(), imageFile: file },
      {
        onSuccess: () => {
          toast.success(t("reminders.modal.saved"))
          onOpenChange(false)
        },
        onError: () => {
          toast.error(t("reminders.modal.saveFailed"))
        },
      },
    )
  }

  const handleUseRecent = async () => {
    if (!selectedRecentId) return

    const recentImg = recentImages.find(img => img.id === selectedRecentId)
    if (!recentImg) return

    const file = await base64ToFile(recentImg.image, "image/png", recentImg.indexNumber)
    await markImageUsed(recentImg.generationId, recentImg.indexNumber)
    createReminder(
      { text: text.trim(), imageFile: file },
      {
        onSuccess: () => {
          toast.success(t("reminders.modal.saved"))
          onOpenChange(false)
        },
        onError: () => {
          toast.error(t("reminders.modal.saveFailed"))
        },
      },
    )
  }

  const isUseDisabled =
    (selectedIndex === null && selectedRecentId === null) || generatedImages.length === 0

  const errorMessage = error ? (error as { message?: string })?.message || error.message : null

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        showCloseButton={!isMobile}
        className="h-full max-w-full md:h-auto md:max-h-[90vh]  md:rounded-tr-2xl md:rounded-bl-2xl bg-[#020202] md:bg-white/6 border-0 md:border overflow-y-auto p-0 md:p-4"
        onInteractOutside={e => e.preventDefault()}
      >
        <div className="hidden md:block">
          <DialogHeader className="pt-5 px-3.5">
            <DialogTitle className="text-[24px] font-semibold uppercase">
              {t("reminders.modal.title")}
            </DialogTitle>
            <DialogDescription className="text-[14px] text-muted-foreground leading-[25px]">
              {t("reminders.modal.subtitle")}
            </DialogDescription>
          </DialogHeader>
        </div>

        <div className="space-y-5">
          <div className="md:hidden sticky top-0 left-0 right-0 z-10 bg-[#020202] px-5 pt-3">
            <div className="flex justify-between items-center">
              <button
                type="button"
                onClick={() => onOpenChange(false)}
                className="p-2 -ml-2 cursor-pointer"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button type="button" className="p-2 -mr-2 cursor-pointer">
                    <MoreVertical className="w-5 h-5" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="min-w-[160px]">
                  <DropdownMenuItem
                    onClick={() => window.open("mailto:support@myrituality.com", "_self")}
                  >
                    <HelpCircle className="w-4 h-4" />
                    Help Center
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
            <div className="pt-5 md:px-4 md:pt-2">
              <h2 className="text-lg leading-normal md:text-[24px] font-semibold uppercase">
                {t("reminders.modal.title")}
              </h2>
              <p className="text-sm text-muted-foreground leading-6.25 mt-2.5 md:mt-1">
                {t("reminders.modal.subtitle")}
              </p>
            </div>
          </div>
          {/* Text Input Section */}
          <div className="space-y-2.5 px-5 md:px-3">
            <label className="text-[12px] font-medium uppercase leading-[12px]">
              {t("reminders.modal.inputLabel")} *
            </label>
            <input
              type="text"
              value={text}
              onChange={e => setText(e.target.value.slice(0, MAX_TEXT_LENGTH))}
              onKeyDown={e => {
                if (e.key === "Enter" && !hasGenerated && text.trim().length >= 3) {
                  e.preventDefault()
                  handleGenerate()
                }
              }}
              placeholder={t("reminders.modal.inputPlaceholder")}
              disabled={isLoading || isCreating}
              className="w-full min-h-[35px] rounded-none border-b border-border bg-transparent py-1 text-[12px] text-primary placeholder:text-muted-foreground focus:outline-none resize-none leading-[20px]"
            />
            <p className="text-xs text-muted-foreground">
              {text.length}/{MAX_TEXT_LENGTH}
            </p>
          </div>

          {/* Error Message */}
          {errorMessage && (
            <div className="p-3 rounded-lg bg-destructive/10 text-destructive text-sm">
              {errorMessage}
            </div>
          )}

          {/* Image Grid */}
          {isLoading ? (
            <div className="px-2">
              <div className="h-[302.5px] bg-[#F2F0EB0D] md:bg-base rounded-tl-[30px] rounded-br-[30px] flex items-center justify-center">
                <div className="border border-white p-4 rounded-full">
                  <AIGeneratedIcon className="w-8 h-8 text-white animate-spin" />
                </div>
              </div>
            </div>
          ) : generatedImages.length > 0 ? (
            <div className="px-2">
              <div className="relative h-[302.5px] rounded-tl-[30px] rounded-br-[30px] overflow-hidden w-full">
                <img
                  src={`data:${generatedImages[0].mimeType};base64,${generatedImages[0].base64}`}
                  alt="Generated image"
                  className="w-full h-full object-cover"
                />
                <p className="z-20 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-white text-[24px] font-semibold text-center py-1 px-2 rounded">
                  {text}
                </p>
              </div>
            </div>
          ) : null}

          {/* Action Buttons */}
          <div className="flex flex-col md:flex-row gap-2.5 md:gap-3 px-5 md:px-0 mt-5">
            {!hasGenerated ? (
              <Button
                onClick={handleGenerate}
                disabled={isLoading || !text.trim() || text.trim().length < 3}
                className={`h-12.5 py-3 md:flex-1 ${isLoading && "hidden"}`}
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    {t("reminders.modal.generating")}
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4 mr-2" />
                    {t("reminders.modal.generateImage")}
                  </>
                )}
              </Button>
            ) : (
              <>
                <Button
                  variant="outline"
                  onClick={handleGenerate}
                  disabled={isLoading}
                  className="h-12.5 py-3 md:flex-1 hover:stroke-[#000]"
                >
                  <RitualityIcon size={20} className="mr-2 !w-5 !h-5" />
                  {t("reminders.modal.regenerate")}
                </Button>
                <Button
                  onClick={selectedRecentId ? handleUseRecent : handleUseImage}
                  disabled={isUseDisabled || isLoading || isCreating}
                  className="h-12.5 py-3 md:flex-1"
                >
                  {isCreating ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      {t("reminders.modal.saving")}
                    </>
                  ) : (
                    t("reminders.modal.use")
                  )}
                </Button>
              </>
            )}
          </div>

          {/* Recent Generations */}
          {recentImages.length > 0 && (
            <div className="space-y-2 md:block hidden">
              <p className="text-sm font-medium text-muted-foreground">
                {t("reminders.modal.recentGenerations")}
              </p>
              <Carousel
                data={recentImages}
                renderItem={img => (
                  <button
                    type="button"
                    onClick={async () => {
                      setSelectedRecentId(img.id)
                      setSelectedIndex(null)
                      const file = await base64ToFile(img.image, "image/png", img.indexNumber)
                      await markImageUsed(img.generationId, img.indexNumber)
                      createReminder(
                        { text: text.trim(), imageFile: file },
                        {
                          onSuccess: () => {
                            toast.success(t("reminders.modal.saved"))
                            onOpenChange(false)
                          },
                          onError: () => {
                            toast.error(t("reminders.modal.saveFailed"))
                          },
                        },
                      )
                    }}
                    className={`relative w-22 h-16 rounded-lg overflow-hidden border-2 transition-all cursor-pointer focus:outline-none ${
                      selectedRecentId === img.id
                        ? "border-primary ring-2 ring-primary/20"
                        : "border-transparent hover:border-border"
                    }`}
                  >
                    <img
                      src={`data:image/png;base64,${img.image}`}
                      alt="Recent generated"
                      className="w-full h-full object-cover"
                    />
                  </button>
                )}
                itemWidth="88px"
                contentClassName="gap-2 touch-pan-x"
                opts={{ dragFree: true, loop: false, containScroll: "trimSnaps" }}
              />
            </div>
          )}

          {/* Mobile Recent Generations - shown during generation */}
          {recentImages.length > 0 && hasGenerated && (
            <div className="md:hidden space-y-2.5 mt-5 px-4">
              <p className="text-sm font-medium text-muted-foreground">
                {t("reminders.modal.recentGenerations")}
              </p>
              <Carousel
                data={recentImages}
                renderItem={img => (
                  <button
                    type="button"
                    onClick={async () => {
                      setSelectedRecentId(img.id)
                      setSelectedIndex(null)
                      const file = await base64ToFile(img.image, "image/png", img.indexNumber)
                      await markImageUsed(img.generationId, img.indexNumber)
                      createReminder(
                        { text: text.trim(), imageFile: file },
                        {
                          onSuccess: () => {
                            toast.success(t("reminders.modal.saved"))
                            onOpenChange(false)
                          },
                          onError: () => {
                            toast.error(t("reminders.modal.saveFailed"))
                          },
                        },
                      )
                    }}
                    className={`relative w-16 h-12 rounded-tl-xl rounded-br-xl overflow-hidden border-2 transition-all cursor-pointer focus:outline-none flex-shrink-0 ${
                      selectedRecentId === img.id
                        ? "border-primary ring-1 ring-primary/20"
                        : "border-transparent hover:border-border"
                    }`}
                  >
                    <img
                      src={`data:image/png;base64,${img.image}`}
                      alt="Recent generated"
                      className="w-full h-full object-cover"
                    />
                  </button>
                )}
                itemWidth="64"
                contentClassName="gap-2 touch-pan-x"
                opts={{ dragFree: true, loop: false, containScroll: "trimSnaps" }}
              />
            </div>
          )}

          {/* Rules - visible only before generation */}
          {!isLoading && !hasGenerated && (
            <div className="md:hidden p-5 rounded-[20px] border m-5 border-dashed border-[#FFFFFF1A] bg-[#101010] space-y-3.75">
              <p className="text-[13px] font-semibold leading-6.25 text-primary">
                {t("reminders.modal.rules")}
              </p>
              <div className="mt-3.75 flex flex-col">
                {(t("reminders.modal.rulesItems", { returnObjects: true }) as string[]).map(
                  (rule, index) => (
                    <span
                      key={index}
                      className="text-sm leading-6.25 text-[#F2F0EBE6] before:content-['•'] before:mr-2"
                    >
                      {rule}
                    </span>
                  ),
                )}
              </div>
              <p className="text-[13px] font-semibold leading-6.25 text-primary">
                {t("reminders.modal.examples")}
              </p>
              <div className="mt-3.75 flex flex-col">
                {(t("reminders.modal.examplesItems", { returnObjects: true }) as string[]).map(
                  (example, index) => (
                    <span
                      key={index}
                      className="text-sm leading-6.25 text-[#F2F0EBE6] before:content-['•'] before:mr-2"
                    >
                      {example}
                    </span>
                  ),
                )}
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
